import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { toCSS } from '../../helpers/styles';

export const ANIMATED_ELEMENT_TYPES = {
  CIRCLE: 'CIRCLE',
  PATH: 'PATH',
  TEXT: 'TEXT',
  IMAGE: 'IMAGE',
  RECTANGLE: 'RECTANGLE',
};

interface IAnimatePath {
  children: React.ReactNode;
  values?: Array<any>;
  duration?: number;
  transitionPoints?: any;
  pauseAnimation?: boolean;
  id: any;
}

interface AnimatedTransitionObject {
  x?: any;
  y?: any;
  posX?: any;
  posY?: any;
  d?: any;
  cx?: any;
  cy?: any;
  transform?: string;
}

interface IAnimatedElement {
  elementType: string;
  duration?: number;
  transitionObject: AnimatedTransitionObject;
  pauseAnimation?: boolean;
  pointName?: string; //styled text
  stroke?: string; // stroke color
  fontSize?: string | number;
  fontWeight?: string;
  textAnchor?: string;
  x?: number; // text position
  y?: number; // text position
  r?: number; // circle radius
  fill?: string; // fill color
  strokeWidth?: string | number; // stroke width "0" | 0
  pointerEvents?: any;
  id?: any;
  width?: number;
  height?: number;
  'data-area'?: string;
  href?: string;
  onClick?: (hookProperties: any) => void;
  onContextMenu?: (hookProperties: any) => void;
  'fill-opacity'?: string | number;
  strokeDasharray?: string;
}

const StyledCircle = styled.circle<any>`
  ${({ transition }) => `
  transition: ${transition};
`};
`;

const StyledPath = styled.path<any>`
  ${({ transition }) => `
    transition: ${transition};
`};
`;

const StyledText = styled.text<any>`
  ${({ transformVal, transition }) => `
    transform: ${transformVal};
    transition: ${transition};
`};
`;

const StyledImage = styled.image<any>`
  cursor: pointer;
  ${({ x, y, transition }) => `
        x: ${x};
        y: ${y};
        transition: ${transition};
`}
`;

const StyledRect = styled.rect<any>`
  ${({ x, y, transition }) => `
      x: ${x};
      y: ${y};
      transition: ${transition};
`};
`;

export const AnimatedElement = (props: IAnimatedElement) => {
  const {
    elementType,
    duration,
    transitionObject,
    pauseAnimation,
    ...restProps
  } = props;
  const animatedRef = useRef(null);
  const [transitionProps, setTransitionProps] = useState(transitionObject);
  const [transition, setTransition] = useState('');

  useEffect(() => {
    if (animatedRef) {
      const currRef = animatedRef.current as any; // element current ref
      const properties = Object.keys(transitionObject);
      const tProps = {};
      let t = '';
      const computedStyle = window.getComputedStyle(currRef); // current computed style of the element

      if (pauseAnimation) {
        //if lesson is paused or has multimedia text
        properties.forEach((item) => {
          const computedValue = computedStyle.getPropertyValue(item); // getting computed value of the properties on which transition needs to be applied
          if (elementType === ANIMATED_ELEMENT_TYPES.PATH) {
            tProps[item] = computedValue.split('"')[1];
          } else {
            tProps[item] = computedValue; // setting current computed value
          }
        });
      } else {
        properties.forEach((item, index) => {
          tProps[item] = transitionObject[item]; // setting initial value on which transition should apply
          t = !!duration
            ? index === 0
              ? `${item} ${duration}ms ease`
              : `${t}, ${item} ${duration}ms ease`
            : ''; // set transition property
        });
      }
      setTransitionProps(tProps);
      setTransition(t);
    }
  }, [pauseAnimation, transitionObject, duration]);

  if (elementType === ANIMATED_ELEMENT_TYPES.CIRCLE) {
    return (
      <StyledCircle
        ref={animatedRef}
        transition={transition}
        {...restProps}
        {...transitionProps}
      />
    );
  } else if (elementType === ANIMATED_ELEMENT_TYPES.IMAGE) {
    return (
      <StyledImage
        ref={animatedRef}
        {...restProps}
        {...transitionProps}
        transition={transition}
      />
    );
  } else if (elementType === ANIMATED_ELEMENT_TYPES.PATH) {
    return (
      <StyledPath
        ref={animatedRef}
        {...restProps}
        {...transitionProps}
        transition={transition}
      />
    );
  } else if (elementType === ANIMATED_ELEMENT_TYPES.TEXT) {
    const { transform, ...rest } = transitionProps;
    return (
      <StyledText
        ref={animatedRef}
        {...restProps}
        {...rest}
        transformVal={transform}
        transition={transition}
      >
        {restProps?.pointName?.toUpperCase()}
      </StyledText>
    );
  } else if (elementType === ANIMATED_ELEMENT_TYPES.RECTANGLE) {
    return (
      <StyledRect
        ref={animatedRef}
        {...restProps}
        {...transitionProps}
        transition={transition}
      />
    );
  } else {
    return <div />;
  }
};

export default function AnimatePath(props: IAnimatePath) {
  const {
    pauseAnimation = false,
    duration,
    transitionPoints,
    children,
    id,
  } = props;

  const ele: any = document.getElementById(id);

  useEffect(() => {
    if (ele) {
      const properties = Object.keys(transitionPoints);
      if (pauseAnimation) {
        const computedStyle = window.getComputedStyle(ele);
        properties.forEach((item) => {
          const computedValue = computedStyle.getPropertyValue(item);
          ele.setAttribute(item, computedValue);
        });
        ele.style.transition = null;
      } else {
        properties.forEach((item) => {
          ele.setAttribute(item, transitionPoints[item]);
        });
        ele.style.transition = properties
          .map((property: any) => `${property} ${duration}ms ease`)
          .join(',');
      }
    }
  }, [pauseAnimation, transitionPoints, duration]);

  return <>{children}</>;
}

export const AnimateText = function (props: IAnimatePath) {
  const { pauseAnimation = false, duration, values = [], children, id } = props;

  const ele: any = document.getElementById(id);

  useEffect(() => {
    if (ele) {
      if (pauseAnimation) {
        const computedStyle = window.getComputedStyle(ele);
        const transform = computedStyle.getPropertyValue('transform');
        ele.style.transform = transform;
        ele.style.transition = null;
      } else {
        const translateCoordinates = values
          .map((value: any) => toCSS(value))
          .join(',');
        const transform = `translate(${translateCoordinates})`;
        ele.style.transform = transform;
        ele.style.transition = `transform ${duration}ms ease`;
      }
    }
  }, [pauseAnimation, values, duration, ele]);

  return <>{children}</>;
};
